/**
 * @fileoverview Searchable currency selector with flag icons, backed by org settings.
 * @module shared/components/ui/CurrencySelect
 */

import { type ReactElement, useState, useMemo } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { useOrgSettings } from '@/features/settings/context/OrgSettingsProvider'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { getCurrencyFlag } from './currencyUtils'

/**
 * Currency code to symbol mapping.
 * @internal Used by CurrencySelect dropdown to show symbol alongside code.
 */
/* eslint-disable no-restricted-syntax -- Intentional: mapping for dropdown display, not formatting output values */
const CURRENCY_SYMBOLS: Record<string, string> = {
    PLN: 'zł',
    EUR: '€',
    USD: '$',
    GBP: '£',
    CHF: 'Fr',
    JPY: '¥',
    CNY: '¥',
    AUD: 'A$',
    CAD: 'C$',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    CZK: 'Kč',
    HUF: 'Ft',
    RON: 'lei',
    BGN: 'лв',
    HRK: 'kn',
    RUB: '₽',
    UAH: '₴',
    TRY: '₺',
    INR: '₹',
    BRL: 'R$',
    MXN: 'MX$',
    ZAR: 'R',
    SGD: 'S$',
    HKD: 'HK$',
    NZD: 'NZ$',
    KRW: '₩',
    THB: '฿',
    MYR: 'RM',
    IDR: 'Rp',
    PHP: '₱',
    VND: '₫',
    ILS: '₪',
    AED: 'د.إ',
    SAR: '﷼',
}
/* eslint-enable no-restricted-syntax */

/**
 * Currency code to full name mapping for search.
 */
const CURRENCY_NAMES: Record<string, string> = {
    PLN: 'Polish Złoty',
    EUR: 'Euro',
    USD: 'US Dollar',
    GBP: 'British Pound',
    CHF: 'Swiss Franc',
    JPY: 'Japanese Yen',
    CNY: 'Chinese Yuan',
    AUD: 'Australian Dollar',
    CAD: 'Canadian Dollar',
    SEK: 'Swedish Krona',
    NOK: 'Norwegian Krone',
    DKK: 'Danish Krone',
    CZK: 'Czech Koruna',
    HUF: 'Hungarian Forint',
    RON: 'Romanian Leu',
    BGN: 'Bulgarian Lev',
    HRK: 'Croatian Kuna',
    RUB: 'Russian Ruble',
    UAH: 'Ukrainian Hryvnia',
    TRY: 'Turkish Lira',
    INR: 'Indian Rupee',
    BRL: 'Brazilian Real',
    MXN: 'Mexican Peso',
    ZAR: 'South African Rand',
    SGD: 'Singapore Dollar',
    HKD: 'Hong Kong Dollar',
    NZD: 'New Zealand Dollar',
    KRW: 'South Korean Won',
    THB: 'Thai Baht',
    MYR: 'Malaysian Ringgit',
    IDR: 'Indonesian Rupiah',
    PHP: 'Philippine Peso',
    VND: 'Vietnamese Dong',
    ILS: 'Israeli Shekel',
    AED: 'UAE Dirham',
    SAR: 'Saudi Riyal',
}

/**
 * Get symbol for a currency code.
 * Falls back to the ISO code if symbol is not mapped.
 */
function getCurrencySymbol(code: string): string {
    return CURRENCY_SYMBOLS[code] ?? code
}

/**
 * Get full name for a currency code.
 */
function getCurrencyName(code: string): string {
    return CURRENCY_NAMES[code] ?? code
}

export interface CurrencySelectProps {
    /** Current value */
    value: string | undefined
    /** Change handler */
    onValueChange: (value: string) => void
    /** Placeholder text when no value selected */
    placeholder?: string
    /** Whether the select is disabled */
    disabled?: boolean
    /** Additional class names for the trigger */
    className?: string
    /** Compact mode for use inside InputGroup - minimal styling, no border */
    compact?: boolean
}

/**
 * Searchable currency selector with flag icons.
 * Displays allowed currencies from org settings with search/filter capability.
 *
 * @component
 * @example
 * ```tsx
 * <CurrencySelect
 *   value={currency}
 *   onValueChange={setCurrency}
 *   placeholder="Select currency"
 * />
 * ```
 */
export function CurrencySelect({
    value,
    onValueChange,
    placeholder = 'Currency',
    disabled = false,
    className,
    compact = false,
}: CurrencySelectProps): ReactElement {
    const [open, setOpen] = useState(false)
    const { allowedReportingCurrencies, isLoading } = useOrgSettings()

    // Use allowed currencies if available, otherwise show common defaults
    const currencies = useMemo(() => {
        return allowedReportingCurrencies.length > 0
            ? allowedReportingCurrencies
            : ['PLN', 'EUR', 'USD', 'GBP']
    }, [allowedReportingCurrencies])

    if (isLoading) {
        return <Skeleton className={compact ? "h-6 w-16" : "h-9 w-full"} />
    }

    const selectedCurrency = value && currencies.includes(value) ? value : undefined

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant={compact ? "ghost" : "outline"}
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn(
                        compact
                            ? 'h-auto px-2 py-1 gap-1 font-normal hover:bg-transparent'
                            : 'w-full justify-between font-normal',
                        className
                    )}
                >
                    {selectedCurrency ? (
                        <span className="flex items-center gap-1.5">
                            <span className={cn("leading-none", compact ? "text-sm" : "text-base")}>{getCurrencyFlag(selectedCurrency)}</span>
                            <span className="font-medium">{getCurrencySymbol(selectedCurrency)}</span>
                        </span>
                    ) : (
                        <span className="text-muted-foreground">{placeholder}</span>
                    )}
                    <ChevronsUpDown className={cn("shrink-0 opacity-50", compact ? "h-3 w-3" : "ml-2 h-4 w-4")} />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0" align={compact ? "end" : "start"}>
                <Command>
                    <CommandInput placeholder="Search currency..." />
                    <CommandList>
                        <CommandEmpty>No currency found.</CommandEmpty>
                        <CommandGroup>
                            {currencies.map((currency) => (
                                <CommandItem
                                    key={currency}
                                    value={`${currency} ${getCurrencySymbol(currency)} ${getCurrencyName(currency)}`}
                                    onSelect={() => {
                                        onValueChange(currency)
                                        setOpen(false)
                                    }}
                                >
                                    <span className="flex items-center gap-2 flex-1">
                                        <span className="text-base leading-none">{getCurrencyFlag(currency)}</span>
                                        <span className="font-medium min-w-[2.5rem]">{getCurrencySymbol(currency)}</span>
                                        <span className="text-muted-foreground text-xs truncate">
                                            {getCurrencyName(currency)}
                                        </span>
                                    </span>
                                    <Check
                                        className={cn(
                                            'ml-auto h-4 w-4',
                                            selectedCurrency === currency ? 'opacity-100' : 'opacity-0'
                                        )}
                                    />
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}

export default CurrencySelect
